import React, { useState } from 'react';
import ExpandableSection from './ExpandableSection';
import Button from 'shared/components/UC/Button';
import FilePreviewModal from '../FilePreviewModal';
import { EvaluatorApplicationDetails } from '../../types/evaluator';
import FileService from '../../services/fileService';
import { names } from '../../utils/ocde.js';
import { isConsolidated } from '../../utils/classificationUtils';
import { getPublicationLink, identifierTypeLabel } from '../../utils/publications';
import styles from './ApplicationPanel.module.css';

// Rótulo completo del rol (badge) y orden de despliegue: IR, IP, IA, COI.
const ROLE_LABELS: Record<string, string> = {
  IR: 'Investigador Responsable',
  IP: 'Investigador Principal',
  IA: 'Investigador Asociado',
  COI: 'Co-Investigador',
};
const ROLE_ORDER: Record<string, number> = { IR: 0, IP: 1, IA: 2, COI: 3 };

interface ApplicationPanelProps {
  application: EvaluatorApplicationDetails;
}

const ApplicationPanel: React.FC<ApplicationPanelProps> = ({ application }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['project-background']) // Start with first section expanded
  );

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  const getTeamType = (answers: any) => {
    // Determine team type based on doctorate year + month.
    // This logic should match the frontend application logic.
    if (!answers) return 'No determinado';

    // DEV NOTE: PROTOTYPE VALUE — production: Contest.config.classificationThreshold (Date, mes incluido)
    // See src/utils/classificationUtils.ts for threshold definition.

    // Check principal investigator (IR — counts as IP for classification purposes)
    const piDocYear = answers.sectionAnswers?.PROJECT_TEAM?.principalInvestigator?.doctorateYear;
    const piDocMonth = answers.sectionAnswers?.PROJECT_TEAM?.principalInvestigator?.doctorateMonth;
    if (isConsolidated(piDocYear, piDocMonth)) {
      return 'Consolidado';
    }

    // Check co-investigators — only IP role affects classification (IA and COI excluded)
    const coInvestigators = answers.sectionAnswers?.PROJECT_TEAM?.coInvestigators || [];
    const hasConsolidatedMember = coInvestigators.some((coi: any) =>
      coi.role === 'IP' && isConsolidated(coi.doctorateYear, coi.doctorateMonth)
    );

    return hasConsolidatedMember ? 'Consolidado' : 'Emergente';
  };

  const getOecdName = (id: string | number) => {
    if (!id) return null;
    const index = typeof id === 'string' ? parseInt(id) : id;
    return names[index] || null;
  };

  // Previsualización en modal (mismo patrón que la Declaración de Singapur en
  // el Resumen del IR), en vez de abrir una pestaña nueva. Para los archivos
  // mock del prototipo (sin documento asociado) el modal muestra un placeholder.
  const [preview, setPreview] = useState<{
    title: string;
    url: string | null;
    note?: string;
  } | null>(null);

  // Solo se monta el iframe con formatos que el navegador RENDERIZA (pdf,
  // imágenes). Un .xlsx/.docx en un iframe no se muestra: se DESCARGA — que
  // era el bug (Previsualizar descargaba el archivo). Para esos formatos el
  // modal muestra un aviso y la descarga queda solo en el botón Descargar.
  const BROWSER_PREVIEWABLE = /\.(pdf|png|jpe?g|gif|webp|txt)$/i;

  const openPreview = (fileId: string, title: string) => {
    const url = FileService.getDownloadUrl(fileId);
    if (!url || url === '#') {
      setPreview({ title, url: null });
      return;
    }
    const record = application.files?.find((f) => f.id === fileId);
    const name = record?.originalName || url;
    if (BROWSER_PREVIEWABLE.test(name.trim())) {
      setPreview({ title, url });
    } else {
      const ext = (name.match(/\.([a-z0-9]+)$/i)?.[1] || '').toUpperCase();
      setPreview({
        title,
        url: null,
        note: `El navegador no puede previsualizar archivos ${
          ext ? `.${ext.toLowerCase()} ` : ''
        }directamente. Use "Descargar" para revisar el documento.`,
      });
    }
  };

  // Helper method to download files with proper extensions
  const downloadFileWithProperName = async (fileId: string, fallbackName: string) => {
    try {
      // First approach: try to find file in application.files array
      const file = application.files?.find(f => f.id === fileId);
      if (file?.originalName) {
        FileService.downloadFile(fileId, file.originalName);
        return;
      }

      // Second approach: use the new method that tries API fallback
      await FileService.downloadFileWithMetadata(fileId, application.id, fallbackName);
    } catch (error) {
      console.error('Failed to download file:', error);
      // Final fallback: use the provided fallback name
      FileService.downloadFile(fileId, fallbackName);
    }
  };

  // Par homologado de acciones de archivo: "Descargar" y "Previsualizar" con la
  // misma jerarquía (botones chicos del kit). El nombre del archivo lo da el
  // encabezado del bloque o el label opcional de la fila — no el botón.
  const renderFileActions = (
    fileId: string,
    downloadName: string,
    label?: string
  ) => (
    <div className={styles.fileItem}>
      {label && <span className={styles.letterName}>{label}</span>}
      <div className={styles.fileActions}>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => downloadFileWithProperName(fileId, downloadName)}
        >
          Descargar
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => openPreview(fileId, label || downloadName)}
        >
          Previsualizar
        </Button>
      </div>
    </div>
  );

  // Shared derived data for the header and the team accordion.
  const projectInfoHeader =
    application.answers?.sectionAnswers?.PROJECT_INFORMATION || {};
  const projectTitle =
    application.projectTitle || projectInfoHeader.projectTitle || 'No disponible';

  // Clasificación del proyecto (bases Avanza UC 2026): Consolidado -> Categoría B,
  // Emergente -> Categoría A. Label canónico igual al del formulario de equipo.
  const teamKind = getTeamType(application.answers); // 'Consolidado' | 'Emergente'
  const classificationLabel =
    teamKind === 'Consolidado'
      ? 'Categoría B — Equipo Consolidado'
      : 'Categoría A — Equipo Emergente';

  // Contador de tiempo transcurrido desde que el evaluador aceptó (no es un plazo).
  const acceptedAt = application.assignment?.acceptedAt || null;
  const elapsedDays = acceptedAt
    ? Math.max(
        0,
        Math.floor((Date.now() - new Date(acceptedAt).getTime()) / 86400000)
      )
    : null;
  const elapsedText =
    elapsedDays === null
      ? null
      : elapsedDays === 0
        ? 'La evaluación se inició hoy.'
        : `Han transcurrido ${elapsedDays} ${elapsedDays === 1 ? 'día' : 'días'} desde el inicio de la evaluación.`;

  const renderBasicInfo = () => {
    const projectInfo = application.answers?.sectionAnswers?.PROJECT_INFORMATION || {};

    return (
      <div className={styles.basicInfoGrid}>
        <div className={`${styles.infoItem} ${styles.infoItemFull}`}>
          <label>Título del Proyecto:</label>
          <span>{application.projectTitle || projectInfo.projectTitle || 'No disponible'}</span>
        </div>
        <div className={styles.infoItem}>
          <label>Proyecto Interdisciplinario:</label>
          <span>{projectInfo.interdisciplinaryProject || 'No especificado'}</span>
        </div>
        <div className={styles.infoItem}>
          <label>Proyecto Transdisciplinario:</label>
          <span>{projectInfo.transdisciplinaryProject || 'No especificado'}</span>
        </div>
        <div className={styles.infoItem}>
          <label>Enfoque de Género:</label>
          <span>{projectInfo.genderApproachProject || 'No especificado'}</span>
        </div>
        <div className={styles.infoItem}>
          <label>Ciencia Abierta:</label>
          <span>{projectInfo.openScience || 'No especificado'}</span>
        </div>
        <div className={styles.infoItem}>
          <label>Palabras Clave:</label>
          <span>
            {projectInfo.keywords
              ? projectInfo.keywords
                  .split(/[;,]/)
                  .map((k: string) => k.trim())
                  .filter(Boolean)
                  .join(', ')
              : 'No disponibles'}
          </span>
        </div>
        <div className={styles.infoItem}>
          <label>Línea de investigación:</label>
          <span>{projectInfo.evaluationArea || 'No especificada'}</span>
        </div>
        <div className={`${styles.infoItem} ${styles.infoItemFull}`}>
          <label>Clasificación OCDE:</label>
          <span>
            {(projectInfo.oecdArea && (() => {
              const areaName = getOecdName(projectInfo.oecdArea);
              const subareaName = projectInfo.oecdSubarea ? getOecdName(projectInfo.oecdSubarea) : null;
              const disciplineName = projectInfo.oecdDiscipline ? getOecdName(projectInfo.oecdDiscipline) : null;

              let result = areaName || projectInfo.oecdArea;
              if (subareaName) {
                result += ` > ${subareaName}`;
              }
              if (disciplineName) {
                result += ` > ${disciplineName}`;
              }
              return result;
            })()) || 'No especificada'}
          </span>
        </div>
        {(projectInfo.sdgGoals && projectInfo.sdgGoals.length > 0) && (
          <div className={styles.infoItem}>
            <label>Objetivos de Desarrollo Sostenible:</label>
            <span>{projectInfo.sdgGoals.join(', ')}</span>
          </div>
        )}
        {projectInfo.executionRegions && projectInfo.executionRegions.length > 0 && (
          <div className={styles.infoItem}>
            <label>Regiones de Ejecución:</label>
            <span>{projectInfo.executionRegions.join(', ')}</span>
          </div>
        )}
      </div>
    );
  };

  const renderProjectContent = () => {
    const content = application.answers?.sectionAnswers?.PROJECT_DESCRIPTION || {};

    return (
      <div className={styles.contentSection}>
        {content.executiveSummary && (
          <div className={styles.textContent}>
            <h4>Resumen Ejecutivo</h4>
            <div className={styles.textBox}>
              <p>{content.executiveSummary}</p>
              <small className={styles.charCount}>
                {content.executiveSummary.length} caracteres
              </small>
            </div>
          </div>
        )}

        {content.projectDocument && (
          <div className={styles.fileSection}>
            <h4>Documento del Proyecto</h4>
            {renderFileActions(
              content.projectDocument,
              'Documento_del_Proyecto.pdf'
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTeamInfo = () => {
    const teamData = application.answers?.sectionAnswers?.PROJECT_TEAM || {};
    const pi = teamData.principalInvestigator || {};
    const coInvestigators = teamData.coInvestigators || [];
    // IR siempre primero, luego IP, IA y COI (orden estable dentro de cada rol).
    const members = [{ ...pi, role: 'IR' }, ...coInvestigators].sort(
      (a: any, b: any) => (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
    );

    return (
      <div className={styles.teamSection}>
        <div className={styles.detailBlock}>
          <span className={styles.classificationBadge}>{classificationLabel}</span>
        </div>

        {members.map((m: any, index: number) => (
          <div key={index} className={styles.memberCard}>
            <div className={styles.memberInfo}>
              <div className={styles.memberName}>
                {m.name || 'No disponible'}
                <span className={styles.roleTag}>{ROLE_LABELS[m.role] || m.role}</span>
              </div>
              <div className={styles.memberDetails}>
                <span>Email: {m.email || 'No disponible'}</span>
                <span>
                  {m.documentType === 'passport' ? 'Pasaporte' : 'RUN'}: {m.run || 'No disponible'}
                </span>
                {m.role === 'COI' ? (
                  <>
                    <span>Tipo de Institución: {m.institutionType || 'No especificada'}</span>
                    <span>Institución: {m.affiliation || 'No especificada'}</span>
                  </>
                ) : (
                  <>
                    <span>Afiliación: Pontificia Universidad Católica de Chile</span>
                    {m.facultadUC && <span>Facultad: {m.facultadUC}</span>}
                    {m.unidadAcademicaUC && <span>Unidad Académica: {m.unidadAcademicaUC}</span>}
                  </>
                )}
                {m.doctorateYear && (
                  <span>
                    Año de Doctorado: {m.doctorateMonth ? `${m.doctorateMonth}/${m.doctorateYear}` : m.doctorateYear}
                    ({isConsolidated(m.doctorateYear, m.doctorateMonth) ? 'Consolidado' : 'Emergente'})
                  </span>
                )}
              </div>
              {m.role === 'COI' && m.commitmentLetter && (
                <div className={styles.fileSection}>
                  {renderFileActions(
                    m.commitmentLetter,
                    `Carta_Compromiso_${m.name || 'COI'}.pdf`,
                    'Carta de Compromiso'
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderBudgetInfo = () => {
    const budget = application.answers?.sectionAnswers?.PROJECT_RESOURCES?.budget || {};
    return (
      <div className={styles.budgetSection}>
        {budget.detailedFile && (
          <div className={styles.fileSection}>
            <h4>Presupuesto Detallado (Excel)</h4>
            {renderFileActions(
              budget.detailedFile,
              'Presupuesto_Detallado.xlsx'
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPublications = () => {
    const attachments = application.answers?.sectionAnswers?.PROJECT_ATTACHMENTS || {};
    const ipCvData = attachments.ipCvData || [];
    const institutionLetters = attachments.institutionCommitmentLetters || [];

    return (
      <div className={styles.publicationsSection}>
        {ipCvData.length > 0 && (
          <div className={styles.cvSection}>
            <h4>Información de Investigadores Principales</h4>
            {ipCvData.map((cvData: any, index: number) => (
              <div key={index} className={styles.cvCard}>
                <h5>{cvData.investigatorName}</h5>

                {cvData.cvNarrative && (
                  <div className={styles.cvNarrative}>
                    <h6>CV Narrativo</h6>
                    <div className={styles.textBox}>
                      <p>{cvData.cvNarrative}</p>
                      <small className={styles.charCount}>
                        {cvData.cvNarrative.length} caracteres
                      </small>
                    </div>
                  </div>
                )}

                {cvData.publications && cvData.publications.length > 0 && (
                  <div className={styles.publicationsList}>
                    <h6>Publicaciones (hasta 5)</h6>
                    {cvData.publications.map((pub: any, pubIndex: number) => {
                      const link = getPublicationLink(pub);
                      return (
                        <div key={pubIndex} className={styles.publicationItem}>
                          {/* Mismo formato que la tabla del IR: título, fecha e
                              identificador con el código como enlace (azul). */}
                          {pub.title && (
                            <span className={styles.publicationTitle}>
                              {pub.title}
                            </span>
                          )}
                          <div className={styles.publicationMeta}>
                            {pub.publishedAt && (
                              <span className={styles.publicationDate}>
                                {formatDate(pub.publishedAt)}
                              </span>
                            )}
                            {pub.identifierValue ? (
                              <span className={styles.publicationIdentifier}>
                                <span
                                  className={styles.publicationIdentifierType}
                                >
                                  {identifierTypeLabel(pub.identifierType)}
                                </span>
                                {link ? (
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.identifierLink}
                                  >
                                    {pub.identifierValue}
                                  </a>
                                ) : (
                                  <code>{pub.identifierValue}</code>
                                )}
                              </span>
                            ) : link ? (
                              // Carga manual con enlace pero sin identificador.
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.identifierLink}
                              >
                                Ver publicación
                              </a>
                            ) : null}
                            {/* A1: acceso al archivo adjunto siempre que exista
                                (único caso con archivo: carga manual). */}
                            {pub.fileId && (
                              <button
                                type="button"
                                onClick={() =>
                                  openPreview(
                                    pub.fileId,
                                    pub.title || 'Archivo adjunto'
                                  )
                                }
                                className={styles.previewLink}
                              >
                                Ver archivo adjunto
                              </button>
                            )}
                            {!link && !pub.fileId && !pub.identifierValue && (
                              <span className={styles.doiLabel}>
                                Sin enlace de acceso
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {cvData.doiFile && (
                  <div className={styles.fileSection}>
                    <h6>Declaración de Obligación Interna (DOI)</h6>
                    {renderFileActions(cvData.doiFile, 'DOI.pdf')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {institutionLetters.length > 0 && (
          <div className={styles.institutionSection}>
            <h4>Cartas de Apoyo Institucional</h4>
            {institutionLetters.map((letter: string, index: number) => (
              <React.Fragment key={index}>
                {renderFileActions(
                  letter,
                  `Carta_Apoyo_${index + 1}.pdf`,
                  `Carta de Apoyo ${index + 1}`
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.applicationTitle}>
          Postulación folio{' '}
          <span className={styles.folioInline}>{application.applicationNumber}</span>
        </h3>
        <p className={styles.projectTitleHeader}>{projectTitle}</p>

        {acceptedAt && (
          <div className={styles.evalStatus}>
            <span className={styles.detailValue}>
              Invitación a evaluar aceptada el {formatDate(acceptedAt)}
            </span>
            {elapsedText && (
              <span className={styles.evalElapsed}>{elapsedText}</span>
            )}
          </div>
        )}
      </div>

      <div className={styles.sectionsContainer}>
        <ExpandableSection
          id="project-background"
          title="Información del Proyecto"
          isExpanded={expandedSections.has('project-background')}
          onToggle={() => toggleSection('project-background')}
        >
          {renderBasicInfo()}
        </ExpandableSection>

        <ExpandableSection
          id="project-content"
          title="Contenido del Proyecto"
          isExpanded={expandedSections.has('project-content')}
          onToggle={() => toggleSection('project-content')}
        >
          {renderProjectContent()}
        </ExpandableSection>

        <ExpandableSection
          id="research-team"
          title="Equipo de Investigación"
          isExpanded={expandedSections.has('research-team')}
          onToggle={() => toggleSection('research-team')}
        >
          {renderTeamInfo()}
        </ExpandableSection>

        <ExpandableSection
          id="budget"
          title="Presupuesto"
          isExpanded={expandedSections.has('budget')}
          onToggle={() => toggleSection('budget')}
        >
          {renderBudgetInfo()}
        </ExpandableSection>

        <ExpandableSection
          id="publications"
          title="Publicaciones y CV"
          isExpanded={expandedSections.has('publications')}
          onToggle={() => toggleSection('publications')}
        >
          {renderPublications()}
        </ExpandableSection>
      </div>

      {preview && (
        <FilePreviewModal
          title={preview.title}
          fileUrl={preview.url}
          placeholderText={preview.note}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default ApplicationPanel;